<script setup lang="ts">
import type { PartnerDocumentDto, PartnerDto } from "mws-finance-ts-sdk";
import { computed, ref } from "vue";

import BaseIcon from "@/common/components/icons/BaseIcon.vue";
import TableVue from "@/common/components/table/TableVue.vue";
import type { TableColumn } from "@/common/components/table/types";
import { getProtectedUrl } from "@/common/utils/getProtectedUrl";
import { useListPartnerDocumentsQuery } from "@/partners/api/queries";
import PartnerDocumentActionMenu from "@/partners/components/PartnerDocumentActionMenu.vue";
import UploadPartnerDocumentModal from "@/partners/components/UploadPartnerDocumentModal.vue";
import UserDisplayName from "@/users/public/components/UserDisplayName.vue";
import { useCurrentUser } from "@/users/public/composables";

definePage({
  meta: {
    auth: ["PartnerDocuments.Read"],
  },
});

const props = defineProps<{
  partner: PartnerDto;
}>();
const partnerId = computed(() => props.partner.id);

const showUploadModal = ref(false);
const { data: documents, isPending } = useListPartnerDocumentsQuery(partnerId);

const { userHasPermission } = useCurrentUser();
const columns: TableColumn<PartnerDocumentDto>[] = [
  {
    label: "Name",
    key: "name",
    type: "text",
  },
  {
    label: "Date Uploaded",
    key: "dateUploaded",
    type: "date",
  },
  { label: "Uploaded By", key: "uploadedBy", type: "slot" },
  { label: "Download", key: "documentUrl", type: "slot" },
];

const onDownload = async (documentUrl: string) => {
  const protectedUrl = await getProtectedUrl(documentUrl);
  window.open(protectedUrl);
};
</script>

<template>
  <ContentLoader v-if="isPending" />
  <TableVue
    v-if="documents"
    :data="documents"
    title="Documents"
    :main-button="
      userHasPermission('PartnerDocuments.Write')
        ? 'Upload Document'
        : undefined
    "
    :is-pending="isPending"
    :columns="columns"
    @main-button-click="showUploadModal = true"
  >
    <template #documentUrl="{ item }">
      <div
        class="flex cursor-pointer"
        @click="onDownload(item.documentUrl)"
      >
        <p class="px-1 font-medium text-indigo-600 hover:text-indigo-500">
          Download
        </p>
        <BaseIcon
          name="heroicons:arrow-down-tray"
          class="size-5"
          aria-hidden="true"
        />
      </div>
    </template>
    <template #rowActions="{ item }">
      <PartnerDocumentActionMenu
        :partner-id="partnerId"
        :document-id="item.id"
        :document-name="item.name"
      />
    </template>
    <template #uploadedBy="{ item }">
      <UserDisplayName :user-id="item.uploadedBy" />
    </template>
  </TableVue>

  <UploadPartnerDocumentModal
    v-model="showUploadModal"
    :partner-id="partnerId"
  />
</template>
